import { CacheAdapter, getCacheAdapter } from "@/lib/vercel/kv";
import { TTL } from "@/lib/vercel/kv/ttl";
import { CacheEvents } from "@algolia/client-common";
import { createTranslator } from "short-uuid";
import { v5 as uuidv5 } from "uuid";

const NAMESPACE = "677fe6f2-15e5-46d4-932d-181f1a4506fc";

const REQUEST_TYPES = {
	SEARCH: "search",
	QUERY_SUGGESTIONS: "query_suggestions",
	RECOMMENDATIONS: "recommendations",
	UNKNOWN: "unknown",
} as const;

type RequestType = (typeof REQUEST_TYPES)[keyof typeof REQUEST_TYPES];

const REQUEST_TYPE_TTL = {
	[REQUEST_TYPES.SEARCH]: TTL.ONE_HOUR,
	[REQUEST_TYPES.QUERY_SUGGESTIONS]: TTL.ONE_DAY,
	[REQUEST_TYPES.RECOMMENDATIONS]: TTL.TWO_DAYS,
	[REQUEST_TYPES.UNKNOWN]: TTL.HALF_HOUR,
} as const;

/**
 * Basic in-memory cache for Algolia search results.
 * taken from https://github.com/algolia/algoliasearch-client-javascript/blob/main/packages/client-common/src/cache/createMemoryCache.ts
 */
class AlgoliaSearchCache {
	private indexName: string;

	private isClientSide: boolean;

	private cacheAdapter: CacheAdapter;

	private translator = createTranslator();

	/**
	 * In-flight requests, keyed the same way as the cache.
	 *
	 * Without this, concurrent identical requests all miss and all hit Algolia:
	 * a PDP with three carousels asking for the same trending query fires three
	 * requests on a cold cache. Entries are removed on settle (success AND
	 * failure) so one failure cannot poison a key for the life of the instance.
	 */
	private inFlight = new Map<string, Promise<any>>();

	constructor(indexName: string) {
		this.indexName = indexName;

		this.isClientSide = typeof window !== "undefined";

		this.cacheAdapter = getCacheAdapter(this.isClientSide ? "memory" : "auto");
	}

	private getKeyString(key: Record<string, any> | string) {
		let keyStr = "";

		if (typeof key === "string") {
			keyStr = key;
		} else {
			keyStr = JSON.stringify(key);
		}

		const v5Uuid = uuidv5(keyStr, NAMESPACE);

		const keyUUID = this.translator.fromUUID(v5Uuid);

		return `${this.indexName}__${keyUUID}`;
	}

	private determineRequestType(key: Record<string, any> | string): RequestType {
		if (typeof key === "string") {
			return REQUEST_TYPES.SEARCH;
		}

		const { path, data }: { path: string; data?: any } = key.request || {};

		if (path?.endsWith("recommendations")) {
			return REQUEST_TYPES.RECOMMENDATIONS;
		}

		const { requests = [] } = data || {};

		if (requests.length === 0) {
			return REQUEST_TYPES.UNKNOWN;
		}

		const mainRequest = requests[0];

		if (String(mainRequest.indexName).endsWith("_query_suggestions")) {
			return REQUEST_TYPES.QUERY_SUGGESTIONS;
		}

		const query = String(mainRequest.query);

		if (query.startsWith("__") && query.endsWith("__")) {
			return REQUEST_TYPES.RECOMMENDATIONS;
		}

		return REQUEST_TYPES.SEARCH;
	}

	async get<TValue>(
		key: Record<string, any> | string,
		defaultValue: () => Promise<TValue>,
		events: CacheEvents<TValue> = {
			miss: (): Promise<void> => Promise.resolve(),
		},
	): Promise<TValue> {
		const keyAsString = this.getKeyString(key);

		const cachedValue = await this.cacheAdapter.get<TValue>(keyAsString);

		if (cachedValue !== null) {
			return cachedValue as TValue;
		}

		// a concurrent caller is already fetching this key, so join it rather
		// than issuing a second identical request
		const existing = this.inFlight.get(keyAsString);

		if (existing) {
			return existing as Promise<TValue>;
		}

		const promise = defaultValue();

		// mirrors the original miss path: await the miss handler, then return
		// the original value. `promise` is reused rather than re-invoked so
		// defaultValue is still called exactly once.
		const request = promise
			.then((value: TValue) => events.miss(value))
			.then(() => promise);

		// registered before awaiting so a concurrent get() joins it, and
		// cleared on both paths so a failure is never left in flight
		this.inFlight.set(keyAsString, request);

		try {
			return await request;
		} finally {
			this.inFlight.delete(keyAsString);
		}
	}

	set<TValue>(
		key: Record<string, any> | string,
		value: TValue,
	): Promise<TValue> {
		const keyAsString = this.getKeyString(key);

		const requestType = this.determineRequestType(key);

		const tags = ["algolia", this.indexName, requestType];
		const ttl = REQUEST_TYPE_TTL[requestType];

		this.cacheAdapter.set(keyAsString, value, { ttl, tags });

		return Promise.resolve(value);
	}

	delete(key: Record<string, unknown> | string): Promise<void> {
		const keyAsString = this.getKeyString(key);

		this.cacheAdapter.delete(keyAsString);

		return Promise.resolve();
	}

	clear(): Promise<void> {
		this.cacheAdapter.clear();

		return Promise.resolve();
	}
}

export default AlgoliaSearchCache;
